import { createLayerTemplate, updateLayerTemplateProperties } from '@/api/layerTemplates';
import { getLayerAttributes } from '@/api/layers';
import type { OilProvinceAttributeRange } from '@/api/oilProvinces';
import { filterVisibleLayerAttributes, isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

interface LayerAttributeInfo {
  id: string;
  name: string;
  unit?: string | null;
  type: string;
  description: string | null;
}

interface ProvinceTemplateCreateModalProps {
  open: boolean;
  onClose: () => void;
  provinceId: string;
  provinceName: string;
  ranges: OilProvinceAttributeRange[];
}

type PrefillMode = 'min' | 'max' | 'average';

const formatBound = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

const formatPrefillNumber = (value: number, type: string): string => {
  if (type === 'integer') {
    return String(Math.round(value));
  }
  // Keep a few decimals without trailing noise
  const rounded = Math.round(value * 1e6) / 1e6;
  return String(rounded);
};

export const ProvinceTemplateCreateModal: React.FC<ProvinceTemplateCreateModalProps> = ({
  open,
  onClose,
  provinceId,
  provinceName,
  ranges,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState('');
  const [visibilityStatusId, setVisibilityStatusId] = useState(PRIVATE_UUID);
  const [allAttributes, setAllAttributes] = useState<LayerAttributeInfo[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});

  const visibleRanges = useMemo(
    () =>
      filterVisibleLayerAttributes(
        ranges,
        (rng) => rng.attribute_id,
        (rng) => rng.attribute_name,
      ),
    [ranges],
  );

  const rangesByAttributeId = useMemo(() => {
    const map = new Map<string, OilProvinceAttributeRange>();
    visibleRanges.forEach((rng) => map.set(rng.attribute_id, rng));
    return map;
  }, [visibleRanges]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setSuccess(false);
    setLoading(false);
    setName(`${provinceName} template`);
    setVisibilityStatusId(PRIVATE_UUID);
    setValues({});

    let cancelled = false;
    const load = async () => {
      try {
        setLoadingAttrs(true);
        // getLayerAttributes already filters hidden attributes
        const attributes = await getLayerAttributes();
        if (cancelled) {
          return;
        }
        const sorted = [...attributes]
          .filter((attr) => !isHiddenLayerAttribute(attr.id, attr.name))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAllAttributes(sorted);
        const initial: Record<string, string> = {};
        sorted.forEach((attr) => {
          initial[attr.id] = '';
        });
        setValues(initial);
      } catch (err) {
        console.error('Failed to load attributes:', err);
        if (!cancelled) {
          setError('Failed to load layer attributes');
        }
      } finally {
        if (!cancelled) {
          setLoadingAttrs(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, provinceId, provinceName]);

  const handleValueChange = (attributeId: string, raw: string) => {
    setValues((prev) => ({ ...prev, [attributeId]: raw }));
  };

  const handlePrefill = (mode: PrefillMode) => {
    setValues((prev) => {
      const next = { ...prev };
      allAttributes.forEach((attr) => {
        const rng = rangesByAttributeId.get(attr.id);
        if (!rng) {
          return;
        }
        let value: number | null = null;
        if (mode === 'min') {
          value = rng.min_value;
        } else if (mode === 'max') {
          value = rng.max_value;
        } else {
          value = (rng.min_value + rng.max_value) / 2;
        }
        if (value === null || Number.isNaN(value)) {
          return;
        }
        next[attr.id] = formatPrefillNumber(value, attr.type);
      });
      return next;
    });
  };

  const parseValue = (attr: LayerAttributeInfo, raw: string): string | number | null => {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    if (attr.type === 'float') {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    if (attr.type === 'integer') {
      const n = Number.parseInt(trimmed, 10);
      return Number.isFinite(n) ? n : null;
    }
    return trimmed;
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const created = await createLayerTemplate({
        name: name.trim(),
        description: `Created from oil province “${provinceName}”. Province attribute ranges are estimates.`,
        is_public: visibilityStatusId === PUBLIC_STATUS_UUID,
        visibility_status_id: visibilityStatusId,
      });

      const propertiesUpdate = allAttributes
        .map((attr) => {
          const parsed = parseValue(attr, values[attr.id] ?? '');
          if (parsed === null) {
            return null;
          }
          return { attribute_id: attr.id, value: parsed };
        })
        .filter((p): p is { attribute_id: string; value: string | number } => p !== null);

      if (propertiesUpdate.length > 0) {
        await updateLayerTemplateProperties(created.id, propertiesUpdate);
      }

      setSuccess(true);
    } catch (err) {
      console.error('Failed to create template from province:', err);
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create template from province</DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success">
            Template created successfully. You can find and edit it on the Templates page like any other reservoir template.
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Province: <strong>{provinceName}</strong>
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <TextField
                label="Template name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ flex: '1 1 240px' }}
                disabled={loading || loadingAttrs}
              />
              <FormControl sx={{ flex: '0 1 200px', minWidth: 180 }} disabled={loading || loadingAttrs}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  label="Visibility"
                  value={visibilityStatusId}
                  onChange={(e) => setVisibilityStatusId(e.target.value as string)}
                >
                  <MenuItem value={PRIVATE_UUID}>Private</MenuItem>
                  <MenuItem value={COMPANY_PUBLIC_UUID}>Company Public</MenuItem>
                  {isSuperAdmin && (
                    <MenuItem value={PUBLIC_STATUS_UUID}>Public</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                mb: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle2">Properties</Typography>
                <Typography variant="body2" color="text.secondary">
                  Prefill from province ranges, then edit any values before create.
                </Typography>
              </Box>
              <ButtonGroup size="small" variant="outlined" disabled={loading || loadingAttrs}>
                <Button onClick={() => handlePrefill('min')}>Min</Button>
                <Button onClick={() => handlePrefill('average')}>Average</Button>
                <Button onClick={() => handlePrefill('max')}>Max</Button>
              </ButtonGroup>
            </Box>

            {loadingAttrs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180 }}>Attribute</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Min</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Max</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allAttributes.map((attr) => {
                      const rng = rangesByAttributeId.get(attr.id);
                      return (
                        <TableRow key={attr.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {attr.name}
                            </Typography>
                            {attr.unit && (
                              <Typography variant="caption" color="text.secondary">
                                {attr.unit}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {formatBound(rng?.min_value)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {formatBound(rng?.max_value)}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="—"
                              value={values[attr.id] ?? ''}
                              onChange={(e) => handleValueChange(attr.id, e.target.value)}
                              disabled={loading}
                              inputProps={{
                                inputMode: attr.type === 'string' ? 'text' : 'decimal',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || loadingAttrs || !name.trim()}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
